import gql from 'graphql-tag';

const ReferableFragment = `
  ... on Page {
    id
    title
    path
    dubbotUrl
    inboundReferencesCount
  }
  ... on Asset {
    id
    path
    dubbotUrl
    friendlyFilesize
    inboundReferencesCount
  }
`;

const ReferrerAttributes = `
  referrerId
  referrerType
  referrerUrl
  referrerUrlHash
  referrerAppUrl
  referrer {
    ${ReferableFragment}
  }
`;

const TargetAttributes = `
  targetUrl
  targetUrlHash
  target {
    ${ReferableFragment}
  }
`;

const ReferenceCoreAttributes = `
  category
  contentType
  filesize
  friendlyFilesize
  httpStatus
`;

export const FullReferenceAttributes = `
  ${ReferenceCoreAttributes}
  id
  siteId
  ${ReferrerAttributes}
  ${TargetAttributes}
`;

export const InboundReferenceAttributes = `
  ${ReferenceCoreAttributes}
  id
  siteId
  ${ReferrerAttributes}
`;

export const OutboundReferenceAttributes = `
  ${ReferenceCoreAttributes}
  id
  siteId
  ${TargetAttributes}
`;


const SiteReferenceAttributes = `
  ${ReferenceCoreAttributes}
  siteId
  ${TargetAttributes}
`;

export const SiteImageReferencesCountQueryFragment = `
  imageReferences: siteReferences(
    accountId: $accountId,
    siteId: $siteId,
    page: 1,
    perPage: 1,
    sortBy: referencesCount,
    sortOrder: desc,
    category: image,
    broken: false,
  ) {
    totalEntries
  }
`;

const GroupedReferenceAttributes = `
  ${ReferenceCoreAttributes}
  targetUrl
  targetUrlHash
  referencesCount
`;

export const SiteReferencesQuery = gql`
  query SiteReferencesQuery(
    $accountId: String!,
    $siteId: String!,
    $page: Int!,
    $perPage: Int!,
    $sortBy: ReferencesSortBy,
    $sortOrder: SortOrder,
    $category: ReferencesCategory,
    $filter: ReferencesFilter,
    $subfilter: String,
    $search: String,
    $broken: Boolean,
    $contentType: String
  ) {
    siteReferences(
      accountId: $accountId,
      siteId: $siteId,
      page: $page,
      perPage: $perPage,
      sortBy: $sortBy,
      sortOrder: $sortOrder,
      category: $category,
      filter: $filter,
      subfilter: $subfilter,
      search: $search,
      broken: $broken,
      contentType: $contentType
    ) {
      currentPage
      perPage
      totalEntries
      totalPages
      nodes {
        ${GroupedReferenceAttributes}
      }
    }
    site(accountId: $accountId, siteId: $siteId) {
      id
      name
      enabled
    }
  }
`;

export const SiteReferenceQuery = gql`
  query SiteReferenceQuery(
    $accountId: String!,
    $siteId: String!,
    $targetUrl: String,
    $targetUrlHash: String,
    $page: Int!,
    $perPage: Int!,
    $sortBy: ReferencesSortBy,
    $sortOrder: SortOrder,
    $category: ReferencesCategory,
    $filter: ReferencesFilter,
    $subfilter: String,
    $search: String,
    $broken: Boolean,
    $contentType: String
  ) {
    siteReference(
      accountId: $accountId,
      siteId: $siteId,
      targetUrl: $targetUrl,
      targetUrlHash: $targetUrlHash,
      page: $page,
      perPage: $perPage,
      sortBy: $sortBy,
      sortOrder: $sortOrder,
      category: $category,
      filter: $filter,
      subfilter: $subfilter,
      search: $search,
      broken: $broken,
      contentType: $contentType
    ) {
      currentPage
      perPage
      totalEntries
      totalPages
      ${SiteReferenceAttributes}
      nodes {
        ${FullReferenceAttributes}
      }
    }
    site(accountId: $accountId, siteId: $siteId) {
      id
      name
      enabled
    }
  }
`;

const buildObjectReferencesQuery = (objectClass, direction) => {
  const objectClassLower = objectClass.toLowerCase();
  const directionLower = direction.toLowerCase();
  return gql`
    query ${direction}${objectClass}ReferencesQuery(
      $accountId: String!,
      $${objectClassLower}Id: String!
      $page: Int!,
      $perPage: Int!,
      $sortBy: ReferencesSortBy,
      $sortOrder: SortOrder,
      $category: ReferencesCategory,
      $filter: ReferencesFilter,
      $subfilter: String,
      $search: String,
      $broken: Boolean,
      $contentType: String
    ) {
      ${objectClassLower}(accountId: $accountId, ${objectClassLower}Id: $${objectClassLower}Id) {
        id
        path
        ${objectClassLower === 'page' ? 'title' : ''}
        ${directionLower}References(
          page: $page,
          perPage: $perPage,
          sortBy: $sortBy,
          sortOrder: $sortOrder,
          category: $category,
          filter: $filter,
          subfilter: $subfilter,
          search: $search,
          broken: $broken,
          contentType: $contentType
        ) {
          currentPage
          perPage
          totalEntries
          totalPages
          nodes {
            ${directionLower === 'inbound' ? InboundReferenceAttributes : OutboundReferenceAttributes}
          }
        }
      }
    }
  `;
}

export const InboundAssetReferencesQuery = buildObjectReferencesQuery('Asset', 'Inbound');
export const InboundPageReferencesQuery = buildObjectReferencesQuery('Page', 'Inbound');
export const OutboundAssetReferencesQuery = buildObjectReferencesQuery('Asset', 'Outbound');
export const OutboundPageReferencesQuery = buildObjectReferencesQuery('Page', 'Outbound');

export const ObjectReferenceCountsFragment = `
${['link', 'image', 'script', 'stylesheet'].map(type => `
  ${type}InboundReferences: inboundReferences(page: 1, perPage: 1, filter: category, subfilter: "${type}") {
    totalEntries
  }
  ${type}OutboundReferences: outboundReferences(page: 1, perPage: 1, filter: category, subfilter: "${type}") {
    totalEntries
  }
`).join('\n')}
`;

const getRefCount = ref => ref?.totalEntries || 0;
export const getReferencesTabCount = (data) => ['link', 'script', 'stylesheet', 'image'].reduce((sum, type) => (
  sum + getRefCount(data[`${type}OutboundReferences`]) + getRefCount(data[`${type}InboundReferences`])
), 0);
